/**
 * UPT-2079 L14a (web §2.1 n° 4, §5.3) — polices d'un module généré : LISTE FERMÉE de 22 clés (copie conforme de `presentation-catalog.json` du rest, §4.4).
 *
 * Le Module Spec ne porte qu'une CLÉ de la liste, jamais un nom libre ni une adresse : `ensureModuleFonts` pose UN lien `link#geode-module-fonts` vers
 * `https://fonts.googleapis.com/css2?family=…&display=swap`, construit UNIQUEMENT depuis cette table (même mécanisme que le configurateur de thème,
 * `theme_configurator_context.tsx`, et que `app/styles/tailwind.css`). Les trois piles `system*` n'ont aucun lien. Aucune police n'est déposée ni hébergée.
 * Fonctions pures, sauf `ensureModuleFonts` (le document).
 */

export const MODULE_FONT_FAMILIES = [
  'system', 'system-serif', 'system-mono', 'inter', 'roboto', 'open-sans', 'lato', 'montserrat', 'poppins', 'nunito',
  'source-sans-3', 'work-sans', 'ibm-plex-sans', 'noto-sans', 'merriweather', 'lora', 'playfair-display', 'cinzel', 'oswald',
  'rajdhani', 'orbitron', 'jetbrains-mono',
] as const;
export type ModuleFontFamily = (typeof MODULE_FONT_FAMILIES)[number];
export type ModuleFontWeight = 400 | 500 | 600 | 700;

export const MODULE_FONT_WEIGHTS: readonly ModuleFontWeight[] = [400, 500, 600, 700];

/** Famille générique de repli ; `display` (titrage) se replie sur `sans-serif`. */
export type ModuleFontGeneric = 'sans-serif' | 'serif' | 'monospace' | 'display';

export interface ModuleFontEntry {
  /** Nom CSS affiché (et nom de la famille chez Google Fonts) ; null pour les trois piles système. */
  css: string | null;
  generic: ModuleFontGeneric;
  weights: readonly ModuleFontWeight[];
}

const ALL_WEIGHTS: readonly ModuleFontWeight[] = MODULE_FONT_WEIGHTS;

export const MODULE_FONT_TABLE: Readonly<Record<ModuleFontFamily, ModuleFontEntry>> = {
  system: { css: null, generic: 'sans-serif', weights: ALL_WEIGHTS },
  'system-serif': { css: null, generic: 'serif', weights: ALL_WEIGHTS },
  'system-mono': { css: null, generic: 'monospace', weights: ALL_WEIGHTS },
  inter: { css: 'Inter', generic: 'sans-serif', weights: ALL_WEIGHTS },
  roboto: { css: 'Roboto', generic: 'sans-serif', weights: ALL_WEIGHTS },
  'open-sans': { css: 'Open Sans', generic: 'sans-serif', weights: ALL_WEIGHTS },
  lato: { css: 'Lato', generic: 'sans-serif', weights: ALL_WEIGHTS },
  montserrat: { css: 'Montserrat', generic: 'sans-serif', weights: ALL_WEIGHTS },
  poppins: { css: 'Poppins', generic: 'sans-serif', weights: ALL_WEIGHTS },
  nunito: { css: 'Nunito', generic: 'sans-serif', weights: ALL_WEIGHTS },
  'source-sans-3': { css: 'Source Sans 3', generic: 'sans-serif', weights: ALL_WEIGHTS },
  'work-sans': { css: 'Work Sans', generic: 'sans-serif', weights: ALL_WEIGHTS },
  'ibm-plex-sans': { css: 'IBM Plex Sans', generic: 'sans-serif', weights: ALL_WEIGHTS },
  'noto-sans': { css: 'Noto Sans', generic: 'sans-serif', weights: ALL_WEIGHTS },
  merriweather: { css: 'Merriweather', generic: 'serif', weights: ALL_WEIGHTS },
  lora: { css: 'Lora', generic: 'serif', weights: ALL_WEIGHTS },
  'playfair-display': { css: 'Playfair Display', generic: 'serif', weights: ALL_WEIGHTS },
  cinzel: { css: 'Cinzel', generic: 'serif', weights: ALL_WEIGHTS },
  oswald: { css: 'Oswald', generic: 'display', weights: ALL_WEIGHTS },
  rajdhani: { css: 'Rajdhani', generic: 'display', weights: ALL_WEIGHTS },
  orbitron: { css: 'Orbitron', generic: 'display', weights: ALL_WEIGHTS },
  'jetbrains-mono': { css: 'JetBrains Mono', generic: 'monospace', weights: ALL_WEIGHTS },
};

/** Alias fixés (rest §4.4) : nom mesuré en minuscules, sans guillemets → clé. */
export const MODULE_FONT_ALIASES: Readonly<Record<string, ModuleFontFamily>> = {
  'source sans pro': 'source-sans-3',
  '-apple-system': 'system',
  blinkmacsystemfont: 'system',
  'segoe ui': 'system',
  'system-ui': 'system',
  'ui-sans-serif': 'system',
  helvetica: 'system',
  'helvetica neue': 'system',
  arial: 'system',
  'sans-serif': 'system',
  georgia: 'system-serif',
  times: 'system-serif',
  'times new roman': 'system-serif',
  'ui-serif': 'system-serif',
  serif: 'system-serif',
  menlo: 'system-mono',
  consolas: 'system-mono',
  monaco: 'system-mono',
  courier: 'system-mono',
  'courier new': 'system-mono',
  'ui-monospace': 'system-mono',
  monospace: 'system-mono',
};

export const isModuleFontFamily = (value: unknown): value is ModuleFontFamily =>
  typeof value === 'string' && (MODULE_FONT_FAMILIES as readonly string[]).includes(value);

/**
 * Résolution d'un nom MESURÉ (rest §4.4) : minuscules, guillemets retirés ; égal à une clé, à son `css` en minuscules ou à un alias → la clé ;
 * sinon `system` et `fallback = true` (remarque `presentation.font_fallback`, détail = le nom mesuré).
 */
export const resolveMeasuredFont = (measured: string): { key: ModuleFontFamily; fallback: boolean } => {
  const name = measured.toLowerCase().replace(/["']/g, '').trim();
  if (isModuleFontFamily(name)) return { key: name, fallback: false };
  for (const key of MODULE_FONT_FAMILIES) {
    const css = MODULE_FONT_TABLE[key].css;
    if (css !== null && css.toLowerCase() === name) return { key, fallback: false };
  }
  const alias = Object.prototype.hasOwnProperty.call(MODULE_FONT_ALIASES, name) ? MODULE_FONT_ALIASES[name] : undefined;
  if (alias) return { key: alias, fallback: false };
  return { key: 'system', fallback: true };
};

/** Piles système (reprise de `app/design-system/tokens.css`, `--font-body`). */
const SYSTEM_STACKS: Readonly<Record<'system' | 'system-serif' | 'system-mono', string>> = {
  system: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  'system-serif': 'ui-serif, Georgia, "Times New Roman", serif',
  'system-mono': 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

/** `"<css>", <générique>` ; les piles `system*` sont celles du socle. */
export const fontStack = (key: ModuleFontFamily): string => {
  const entry = MODULE_FONT_TABLE[key];
  if (entry.css === null) return SYSTEM_STACKS[key as 'system' | 'system-serif' | 'system-mono'];
  const generic = entry.generic === 'display' ? 'sans-serif' : entry.generic;
  return `"${entry.css}", ${generic}`;
};

export interface ModuleFontRequest {
  family: ModuleFontFamily;
  weights?: readonly number[];
}

/**
 * Adresse de chargement (une seule feuille Google Fonts) pour les familles demandées, construite UNIQUEMENT depuis la table ; `null` si aucune famille
 * n'a de lien (piles système seules). Poids = ceux demandés, dans {400, 500, 600, 700}, triés et dédoublonnés (défaut 400 et 700).
 */
export const moduleFontsHref = (fonts: readonly ModuleFontRequest[]): string | null => {
  const byFamily = new Map<string, Set<number>>();
  for (const font of fonts) {
    if (!isModuleFontFamily(font.family)) continue;
    const css = MODULE_FONT_TABLE[font.family].css;
    if (css === null) continue;
    const weights = (font.weights ?? [400, 700]).filter((weight): weight is ModuleFontWeight =>
      (MODULE_FONT_WEIGHTS as readonly number[]).includes(weight));
    const set = byFamily.get(css) ?? new Set<number>();
    (weights.length > 0 ? weights : [400, 700]).forEach((weight) => set.add(weight));
    byFamily.set(css, set);
  }
  if (byFamily.size === 0) return null;
  const query = [...byFamily.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([css, weights]) => `family=${css.replace(/ /g, '+')}:wght@${[...weights].sort((a, b) => a - b).join(';')}`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
};

export const MODULE_FONTS_LINK_ID = 'geode-module-fonts';

/** Familles déjà demandées dans cette session (union : plusieurs enveloppes de thème et le sélecteur de polices partagent UN seul lien). */
const requested = new Map<ModuleFontFamily, Set<number>>();

/** Tests : oublie les familles demandées. */
export const resetModuleFontsForTests = (): void => {
  requested.clear();
};

/**
 * Pose (ou met à jour) l'UNIQUE lien `link#geode-module-fonts` pour l'union des familles demandées depuis le début de la session ; sans police à charger
 * (piles système seules), ne pose rien. Sans effet hors navigateur. Ne réécrit pas un `href` identique.
 */
export const ensureModuleFonts = (fonts: readonly ModuleFontRequest[]): void => {
  if (typeof document === 'undefined') return;
  for (const font of fonts) {
    if (!isModuleFontFamily(font.family) || MODULE_FONT_TABLE[font.family].css === null) continue;
    const weights = requested.get(font.family) ?? new Set<number>();
    (font.weights ?? [400, 700]).forEach((weight) => weights.add(weight));
    requested.set(font.family, weights);
  }
  const href = moduleFontsHref([...requested.entries()].map(([family, weights]) => ({ family, weights: [...weights] })));
  if (href === null) return;
  let link = document.getElementById(MODULE_FONTS_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = MODULE_FONTS_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
};
